import { useEffect } from 'react';

export const UNSAVED_CHANGES_MESSAGE =
  'Vous avez des modifications non enregistrées. Si vous quittez cette page, elles seront perdues.';

// Whether the user agrees to leave: always when nothing is pending.
export const confirmLeaving = (isDirty) => !isDirty || window.confirm(UNSAVED_CHANGES_MESSAGE);

/**
 * The browser's own warning while `isDirty`, before the changes are lost:
 * closing or reloading the tab (`beforeunload`, the browser's wording), and
 * following a link of the app to another page (asked before react-router
 * moves). `onLeave` runs once the user agreed, to drop what is abandoned.
 *
 * The browser's back button is not asked: the router has already moved by the
 * time it can be seen.
 */
const useUnsavedChangesWarning = (isDirty, onLeave) => {
  useEffect(() => {
    if (!isDirty) return undefined;
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      // Still what Chrome reads to show its dialog.
      event.returnValue = '';
    };
    // Captured before react-router's own click handler on its <Link>.
    const askBeforeLink = (event) => {
      const link = event.target.closest?.('a[href]');
      if (!link || link.target === '_blank' || event.metaKey || event.ctrlKey) return;
      const sameApp = link.origin === window.location.origin;
      if (sameApp && link.pathname === window.location.pathname) return;
      if (window.confirm(UNSAVED_CHANGES_MESSAGE)) {
        onLeave?.();
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    document.addEventListener('click', askBeforeLink, true);
    return () => {
      window.removeEventListener('beforeunload', warnBeforeUnload);
      document.removeEventListener('click', askBeforeLink, true);
    };
  }, [isDirty, onLeave]);
};

export default useUnsavedChangesWarning;
